
import * as d3 from 'd3';
import moment, { Moment } from 'moment';
import { clearTimeout, setTimeout } from 'timers';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { AnswerType } from '../../shared/contracts/AnswerContract';
import { IEvolution, IEvolutionDataPoint } from '../../shared/contracts/EvolutionContract';
import { IConsultantBarometerKPIResult, IKPIResult } from '../../shared/contracts/KPIContract';

@Component
export default class KpiTrend extends Vue {
    private width: number = 0;
    private height: number = 0;
    private svgElement: any = undefined;
    private margin = { top: 20, right: 80, bottom: 20, left: 40 };
    private legendLineSize = 25;
    private legendSpacing = 8;

    private todayM = moment();

    @Prop() evolution: IEvolution;
    @Prop() currentKpiValue: IConsultantBarometerKPIResult;
    @Watch('evolution', { deep: true })
    onEvolutionChanged(val: IEvolution) {
        setTimeout(() => this.updateTrend(), 0);
    }

    get growthTrend(): IEvolutionDataPoint[] {
        return this.getTrend(AnswerType.Growth);
    }

    get networkTrend(): IEvolutionDataPoint[] {
        return this.getTrend(AnswerType.Network);
    }
    get projectTrend(): IEvolutionDataPoint[] {
        return this.getTrend(AnswerType.Project);
    }

    get rotationTrend(): IEvolutionDataPoint[] {
        return this.getTrend(AnswerType.Rotation);
    }

    mounted() {
        setTimeout(() => {
            this.svgElement = d3.select(this.$el).select('svg');
            this.updateTrend();
            this.addResizeEventListener();
        }, 0);
    }

    /**
     * In the KPI trend, we will always show the graph between 12 months ago and today.
     *
     * We take one sample for each week, to avoid a graph that is too crispy.
     *
     * We also create a data point for 'today'. Today's KPI value is not part of the 'evolution' dataset,
     * but we show its data on the graph anyway (via currentKpiValue Prop)
     */
    private yearTrend(history: IEvolutionDataPoint[], currentKpiResult: IKPIResult): IEvolutionDataPoint[] {
        let result: IEvolutionDataPoint[] = [];
        // Handle case where history is null or undefined
        if (!history || !Array.isArray(history)) {
            return result;
        }
        // following assumes data is sorted
        for (let i = 0; i < history.length; i++) {
            // only keep the first datapoint of each week.
            // Note: wee keep the first, so the graph starts with a point
            const currentPointWeek = moment(history[i].date).isoWeek();
            const previousPointWeek = history[i - 1] && moment(history[i - 1].date).isoWeek();
            if (currentPointWeek !== previousPointWeek) {
                // this is the last datapoint for this week
                result.push({
                    date: moment(history[i].date)
                        .startOf('day')
                        .toDate(),
                    kpiValue: history[i].kpiValue * 100
                });
            }
        }
        // add today's result
        if (currentKpiResult) {
            result.push({
                date: this.getToday()
                    .startOf('day')
                    .toDate(),
                kpiValue: currentKpiResult.kpiValue * 100
            });
        }
        return result;
    }

    private getTrend(answerType: AnswerType): IEvolutionDataPoint[] {
        if (!this.evolution || !this.evolution.data) {
            return [];
        }
        const currentKpiValue = this.currentKpiValue && this.currentKpiValue[answerType];
        return this.yearTrend(this.evolution.data[answerType], currentKpiValue);
    }

    private getToday(): Moment {
        return this.todayM.clone(); // moment.add modifies the original moment object
    }

    private formatDateTick(date: Date) {
        if (d3.timeDay(date) < date) {
            // format hour
            return moment(date).format('HH:mm');
        } else if (d3.timeMonth(date) < date) {
            // format day
            return moment(date).format('MMM Do');
        } else {
            // format month
            return moment(date).format("MMM 'YY");
        }
    }

    private getDateStartingPoint(): Date {
        let twoWeeksAgo = moment().subtract(2, 'weeks');
        if (!this.evolution || !this.evolution.data || !this.evolution.data[AnswerType.Project].length) {
            return twoWeeksAgo.toDate();
        }
        let firstDataPoint = moment(this.evolution.data[AnswerType.Project][0].date);
        return moment.min(twoWeeksAgo, firstDataPoint).toDate();
    }

    private hasData(): boolean {
        // if there is only data for 'today', nothing will show on the graph because you need at least 2 datapoints to draw a line
        // so, if evolution is empty, the graph is empty
        return (
            this.evolution &&
            this.evolution.data &&
            (!!this.evolution.data[AnswerType.Project].length ||
                !!this.evolution.data[AnswerType.Network].length ||
                !!this.evolution.data[AnswerType.Growth].length ||
                !!this.evolution.data[AnswerType.Rotation].length)
        );
    }

    private addResizeEventListener() {
        let timeoutId: any;
        window.addEventListener('resize', () => {
            if (!timeoutId) {
                timeoutId = setTimeout(() => {
                    // Throttle resize
                    this.updateTrend();
                    clearTimeout(timeoutId);
                    timeoutId = undefined;
                }, 100);
            }
        });
    }

    private updateTrend() {
        this.height = 400 - this.margin.top - this.margin.bottom; // Always fixed height
        if (this.svgElement && this.svgElement.node()) {
            const svgClientRect = (this.svgElement.node() as any).getBoundingClientRect();
            const newWidth = svgClientRect.width - this.margin.left - this.margin.right - this.legendLineSize;
            if (newWidth > 0 && this.width !== newWidth) {
                this.width = newWidth;
                this.refreshTrend();
            }
        }
    }

    private refreshTrend() {
        this.svgElement.selectAll('*').remove();

        const svg = this.svgElement
            .append('g')
            .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')');

        // Scales
        const x = d3
            .scaleTime()
            .range([0, this.width] as ReadonlyArray<number>)
            .domain([
                this.getDateStartingPoint(),
                this.getToday()
                    .endOf('day')
                    .toDate()
            ]);

        const y = d3
            .scaleLinear()
            .range([this.height, 0] as ReadonlyArray<number>)
            .domain([0, 100]);

        // Axis
        const xAxis = d3
            .axisBottom(x)
            .tickSize(0)
            .scale(x)
            .tickFormat((d: any, i: number) => this.formatDateTick(d));

        svg.append('g')
            .attr('class', 'x-axis')
            .attr('transform', 'translate(0,' + this.height + ')')
            .style('font-size', '12px')
            .style('font-family', 'Lato, "Helvetica Neue", Helvetica, Arial, sans-serif')
            .call((xAxis as any).ticks(6))
            .selectAll('text')
            .attr('transform', 'translate(0, 15)'); // tick text

        const yAxis = d3
            .axisLeft(y)
            .tickValues([0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100])
            .tickSize(-this.width); // horizontal graph lanes

        svg.append('g')
            .attr('class', 'y-axis')
            .style('font-size', '12px')
            .style('font-family', 'Lato, "Helvetica Neue", Helvetica, Arial, sans-serif')
            .call(yAxis)
            .selectAll('text')
            .attr('transform', 'translate(-10, 0)'); // tick text;

        // Graph lines
        const createPath = (d3 as any)
            .line()
            .x((d: IEvolutionDataPoint) => x(d.date))
            .y((d: IEvolutionDataPoint) => y(d.kpiValue))
            .curve(d3.curveMonotoneX);
        svg.append('path')
            .attr('class', 'kpi-line kpi-project-line')
            .attr('d', createPath(this.projectTrend));

        svg.append('path')
            .attr('class', 'kpi-line kpi-network-line')
            .attr('d', createPath(this.networkTrend));

        svg.append('path')
            .attr('class', 'kpi-line kpi-growth-line')
            .attr('d', createPath(this.growthTrend));

        // Green target bar
        svg.append('rect')
            .attr('x', 0)
            .attr('y', y(100))
            .attr('width', this.width)
            .attr('height', this.height * 0.25)
            .attr('class', 'kpi-trend__bar-green');

        // Legend
        const ordinal = d3
            .scaleOrdinal()
            .domain(['Project', 'Growth', 'Network'] as ReadonlyArray<string>)
            .range(['project', 'growth', 'network'] as ReadonlyArray<string>);

        const legend = svg
            .selectAll('.legend')
            .data(ordinal.domain())
            .enter()
            .append('g')
            .attr('class', 'kpi-trend__legend')
            .attr('transform', (d: any, i: number) => {
                const height = this.legendLineSize + this.legendSpacing;
                const offset = (height * ordinal.domain().length) / 2;
                const horz = this.width + 10;
                const vert = i * height - offset + this.height / 2 + this.margin.top;
                return 'translate(' + horz + ',' + vert + ')';
            });

        legend
            .append('line')
            .attr('x1', 0)
            .attr('x2', this.legendLineSize)
            .attr('y1', -5)
            .attr('y2', -5)
            .attr('class', ordinal);

        legend
            .append('text')
            .attr('x', this.legendLineSize + this.legendSpacing)
            .attr('y', 0)
            .text((d: any) => {
                return d;
            });

        // tracker on mouse over
        let tracker = svg.append('g').attr('class', 'mouse-over-effects');

        // tracker vertical line
        let trackerVerticalLine = tracker
            .append('path')
            .attr('class', 'tracker-vertical-line')
            .style('opacity', '0');

        // tracker date
        const dateTrackRectWidth = 85;
        const dateTrackRectHeigth = 20;
        let trackerDate = tracker.append('g');
        trackerDate
            .append('rect')
            .attr('x', 0)
            .attr('y', y(0) - dateTrackRectHeigth - 5)
            .attr('width', dateTrackRectWidth)
            .attr('height', dateTrackRectHeigth)
            .attr('class', 'tracker-vertical-line-date-rect')
            .style('opacity', '0');

        trackerDate
            .append('text')
            .attr('y', y(0) - 10)
            .attr('class', 'tracker-vertical-line-date-text')
            .attr('transform', 'translate(5, 0)') // tick text
            .style('opacity', '0');

        tracker
            .append('svg:rect') // append a rect to catch mouse movements on canvas
            .attr('width', this.width)
            .attr('height', this.height)
            .attr('fill', 'none')
            .attr('pointer-events', 'all')
            .on('mouseout', () => {
                // on mouse out hide tracker stuff
                d3.selectAll('.tracker-vertical-line').style('opacity', '0');
                d3.selectAll('.tracker-vertical-line-date-rect,.tracker-vertical-line-date-text').style('opacity', '0');
            })
            .on('mouseover', () => {
                // on mouse in show line, circles and text
                d3.selectAll('.tracker-vertical-line').style('opacity', '1');
                d3.selectAll('.tracker-vertical-line-date-rect,.tracker-vertical-line-date-text').style('opacity', '1');
            })
            .on('mousemove', (_: any, index: number, n: d3.ContainerElement[]) => {
                // mouse moving over canvas
                const mouse = d3.mouse(n[index]);

                // move tracker vertical line
                trackerVerticalLine.attr('d', () => {
                    let d = 'M' + mouse[0] + ',' + this.height;
                    d += ' ' + mouse[0] + ',' + 0;
                    return d;
                });

                // move tracker date and set correct date
                trackerDate.selectAll('rect,text').attr('x', () => {
                    return mouse[0] - dateTrackRectWidth / 2;
                });
                trackerDate.select('text').text(() => {
                    let dateAtCurrentPosition = x.invert(mouse[0]);
                    return moment(dateAtCurrentPosition).format("MMM Do 'YY");
                });
            });
    }
}
